import MeetingRoomList from "./Room/MeetingRoomList";
import MeetingRoomTabs from "./Tabs/MeetingRoomTabs";
import MeetingRoomGuidePopupModal from "./Modal/Popup/MeetingRoomGuidePopupModal";

import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { MeetingRoomApi } from "../../system/api/MeetingRoom/MeetingRoomApi";
import {
  DateInfo,
  MeetingRoomEventListInfo,
  MeetingRoomInfo,
  MeetingRoomLocation,
  MeetingRoomStatusInfo,
  OutlookDeepLinkParam,
  RoomColorInfo,
  SelectedLocationInfo,
} from "../../system/types/meetingRoom/MeetingRoom";
import { Yellow100 } from "../../styles/color";
import { MeetingRoomContentBox } from "./MeetingRoomContentStyled";
import { useOutlookDeeplink } from "../../hooks/Outlook/useOutlookDeepLink";
import { useDeviceType } from "../../hooks/useDeviceType";

const MeetingRoomContent = () => {
  // 날짜정보
  const [dateInfo, setDateInfo] = useState<DateInfo>({
    stdDate: dayjs().format("YYYY-MM-DD"),
    curDateTime: dayjs().format("YYYY-MM-DD:HH:mm"),
  });
  const [selectedTab, setSelectedTab] = useState<"week" | "month">("week");
  const [meetingRoomIsLoading, setMeetingRoomIsLoading] = useState(true);
  // 선택된 Tab의 값.
  const [selectedLocationInfo, setSelectedLocationInfo] =
    useState<SelectedLocationInfo>({
      prevLocation: null,
      curLocation: MeetingRoomLocation.IHQ,
    });

  // 선택된 회의실 정보 값
  const [selectedMeetingRoom, setSelectedMeetingRoom] =
    useState<MeetingRoomStatusInfo | null>(null);

  // 예약 정보 목록
  const [meetingRoomEventList, setMeetingRoomEventList] =
    useState<MeetingRoomEventListInfo>({
      initAllEventList: {},
      isInit: false,
      todayEventList: {},
      eventList: {},
    });

  // 회의실 목록
  const [meetingRoomInfoList, setMeetingRoomInfoList] = useState<
    MeetingRoomInfo[]
  >([]);

  const handleSelectedTab = useCallback((tab: "week" | "month") => {
    if (tab !== null) {
      setMeetingRoomIsLoading(true); // 조회 후 false로 변경
      setSelectedTab(tab);
    }
  }, []);

  // Schedule에 보일 색상 목록
  const colorList: RoomColorInfo[] = useMemo(() => {
    const list = [Yellow100];
    const result = meetingRoomInfoList.map((el, idx) => ({
      room: el.displayName,
      color: list[idx % list.length],
    }));

    return result;
  }, [meetingRoomInfoList]);

  const [guidePopupInfo, setGuidePopupInfo] = useState<{
    modalOpen: boolean;
    params: OutlookDeepLinkParam[];
  }>({
    modalOpen: false,
    params: [],
  });

  // 날짜 정보 변경
  const handleChangeDateInfo = useCallback((valueObject: Partial<DateInfo>) => {
    setDateInfo((prev) => ({
      ...prev,
      ...valueObject,
    }));
  }, []);

  // 선택된 위치 변경 (본사, 공장, 벤처)
  const handleSelectedLocation = useCallback((value: MeetingRoomLocation) => {
    setMeetingRoomIsLoading(true); // 조회 후 false로 변경
    setSelectedMeetingRoom(null);
    setMeetingRoomEventList((prev) => ({
      ...prev,
      todayEventList: [] as any,
    }));
    setDateInfo((prev) => ({
      ...prev,
      stdDate: dayjs().format("YYYY-MM-DD"),
    }));
    setSelectedLocationInfo((prev) => ({
      prevLocation: prev.curLocation,
      curLocation: value,
    }));
  }, []);

  // 선택된 회의실 변경
  const handleChangeSelectedMeetingRoom = useCallback(
    (room: MeetingRoomStatusInfo) => {
      setSelectedMeetingRoom(room);
    },
    []
  );

  // EventList 조회.
  const getMeetingRoomEvents = useCallback(async () => {
    try {
      let startDate = "";
      let endDate = "";
      const stdDate = dateInfo.stdDate;

      if (selectedTab === "week") {
        // stdDate가 포함된 주의 시작~끝
        startDate = dayjs(stdDate).startOf("week").format("YYYY-MM-DD");
        endDate = dayjs(stdDate).endOf("week").format("YYYY-MM-DD");
      } else if (selectedTab === "month") {
        // 해당 달의 시작일 (1일)이 포함된 주의 시작~끝
        const monthStartDate = dayjs(stdDate).startOf("month");
        const monthEndDate = dayjs(stdDate).endOf("month");

        startDate = monthStartDate.startOf("week").format("YYYY-MM-DD");
        endDate = monthEndDate.endOf("week").format("YYYY-MM-DD");
      }

      const { curLocation } = selectedLocationInfo;

      const params = {
        startDate,
        endDate,
        location: curLocation,
      };

      const data = await MeetingRoomApi.getMeetingRoomEvents<any>(params);
      setMeetingRoomEventList((prev: any) => ({
        ...prev,
        initAllEventList: data,
        todayEventList:
          selectedLocationInfo.prevLocation === null ||
          selectedLocationInfo.curLocation !==
            selectedLocationInfo.prevLocation ||
          Object.keys(prev.todayEventList).length === 0
            ? data
            : prev.todayEventList,
        eventList: data,
      }));

      if (
        selectedLocationInfo.prevLocation === null ||
        selectedLocationInfo.curLocation !== selectedLocationInfo.prevLocation
      ) {
        setSelectedLocationInfo((prev) => ({
          ...prev,
          prevLocation: prev.curLocation,
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setMeetingRoomIsLoading(false);
    }
  }, [dateInfo, selectedLocationInfo, selectedTab]);

  // 회의실 목록 조회
  const getMeetingRoomList = useCallback(async () => {
    try {
      const params = { location: "all" };
      const data = await MeetingRoomApi.getMeetingRoomList<MeetingRoomInfo[]>(
        params
      );
      const sortedData = [...data].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );
      setMeetingRoomInfoList(sortedData);
    } catch (err) {
      console.error(err);
    } finally {
      setMeetingRoomIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getMeetingRoomList();
  }, [getMeetingRoomList]);

  useEffect(() => {
    getMeetingRoomEvents();
    // 탭 포커싱 시 재조회
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        getMeetingRoomEvents();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dateInfo.stdDate, selectedTab, selectedLocationInfo.curLocation]);

  const { openOutlookPopup } = useOutlookDeeplink();

  const handleOpenGuidePopup = useCallback(
    (status: boolean, params: OutlookDeepLinkParam[]) => {
      setGuidePopupInfo({
        modalOpen: status,
        params,
      });
    },
    []
  );
  // Outlook DeepLinkPopup 열기
  const handleOpenOutlookPopup = useCallback(
    (params: OutlookDeepLinkParam[]) => {
      openOutlookPopup(params, getMeetingRoomEvents);
    },
    [openOutlookPopup, getMeetingRoomEvents]
  );

  const deviceType = useDeviceType();

  return (
    <>
      <MeetingRoomContentBox deviceType={deviceType}>
        <MeetingRoomList
          isLoading={meetingRoomIsLoading}
          meetingRoomInfoList={meetingRoomInfoList}
          dateInfo={dateInfo}
          selectedLocation={selectedLocationInfo.curLocation}
          handleSelectedLocation={handleSelectedLocation}
          selectedMeetingRoom={selectedMeetingRoom}
          handleChangeSelectedMeetingRoom={handleChangeSelectedMeetingRoom}
          meetingRoomEventList={meetingRoomEventList.todayEventList}
          handleOpenOutlookPopup={handleOpenOutlookPopup}
          handleOpenGuidePopup={handleOpenGuidePopup}
        />

        <MeetingRoomTabs
          getMeetingRoomEvents={getMeetingRoomEvents}
          meetingRoomIsLoading={meetingRoomIsLoading}
          selectedTab={selectedTab}
          handleSelectedTab={handleSelectedTab}
          selectedMeetingRoom={selectedMeetingRoom}
          colorList={colorList}
          dateInfo={dateInfo}
          meetingRoomEventList={
            selectedMeetingRoom !== null
              ? meetingRoomEventList.eventList[selectedMeetingRoom.id]
              : []
          }
          handleChangeDateInfo={handleChangeDateInfo}
          handleOpenOutlookPopup={handleOpenOutlookPopup}
          handleOpenGuidePopup={handleOpenGuidePopup}
        />
        <MeetingRoomGuidePopupModal
          guidePopupInfo={guidePopupInfo}
          handleOpenGuidePopup={handleOpenGuidePopup}
          handleOpenOutlookPopup={handleOpenOutlookPopup}
        />
      </MeetingRoomContentBox>
    </>
  );
};

export default MeetingRoomContent;
